// Recharts
import { Cell, Label, Pie, PieChart } from "recharts";

// Context
import { useSalesLogChart } from "@DataPages/context/salesOperations/salesLog/SalesLogChartsContext";

// Helpers
import { firstRegularNumbers } from "@/Common/helpers/number";
import { roundUpToNearest500 } from "@DataPages/helpers/salesOperations/salesLog/utils";

// Components
import AnimatedNeedle from "./Widgets/Speedometer/AnimatedNeedle";
import CustomMoneyLabel from "./Widgets/Speedometer/AnimatedLabel";
import CustomPill from "@DataPages/components/SalesOperations/Tabs/SalesLog/Widgets/Speedometer/CustomPill";

// Types
import { IDynamicPVRInfo, ISalesAnalyticsProjection } from "@DataPages/types/salesOperations/salesLog/model";
import { GridRowId } from "@mui/x-data-grid-premium";

type SpeedometerChartProps = {
	data: ISalesAnalyticsProjection;
	useValues: { original: string; projection?: string };
	maxAxis: number;
	dynamicPVRInfo: IDynamicPVRInfo;
	isHoveringChart: boolean;
	speedometerValue: number;
};

type GetValueParams = {
	isHoveringChart: boolean;
	filteredRows: GridRowId[];
	speedometerValue: number;
	value: number;
};
type UseValue = "frontGross" | "backPVR" | "totalPVR";

/*************************************************** HELPERS ********************************************************/
const BASE_ANGLE = 180;
export function normalizedAngle(value: number, min: number, max: number) {
	if (value >= max) {
		return 0;
	}

	const shiftedValue = (value - min) / (max - min);
	return BASE_ANGLE + shiftedValue * (0 - BASE_ANGLE);
}

function getValue({ isHoveringChart, filteredRows, speedometerValue, value }: GetValueParams): number {
	if (isHoveringChart) {
		return filteredRows?.length ? speedometerValue : 0;
	} else {
		return filteredRows?.length ? value : 0;
	}
}

export default function SpeedometerChart(props: Readonly<SpeedometerChartProps>) {
	const { filteredRows, shouldReCalculateAnalytics } = useSalesLogChart();

	const value = props.isHoveringChart
		? props.speedometerValue
		: Math.round(props?.data?.current?.[props.useValues?.original as UseValue]);
	const currentValue = props.isHoveringChart
		? props.speedometerValue
		: Math.round(props?.data?.current?.[props.useValues?.original as UseValue]);

	const RADIAN = Math.PI / BASE_ANGLE;

	const data = [
		{ name: "A", value: 1, color: "url(#colorA)" },
		{ name: "B", value: 1, color: "url(#colorB)" },
		{ name: "C", value: 1, color: "url(#colorC)" },
	];

	const cx = 150;
	const cy = 200;
	const iR = 130; // Inner radius: as bigger this number is, the pie section will reduce the pie section looking smaller
	const oR = 140; // Outer radius: as bigger this number is, the pie chart will increase in size
	const maxValue = props?.maxAxis * 1000 || 0;

	const SHIFT_VALUE = 5;

	const labelLineAndOutsideLabelColor = "#B3B3B3";
	const renderCustomizedLabel = (args: {
		cx: number;
		cy: number;
		midAngle: number;
		startAngle: number;
		endAngle: number;
		outerRadius: number;
		index: number;
	}) => {
		const { cx: cxValue = 0, cy: cyValue = 0, midAngle, startAngle, endAngle, outerRadius, index } = args;
		const length = outerRadius + 10; // The length of the line, you can adjust this as per your need.

		// Calculate the start and end points of the lines for the start and end angles
		const startLine = {
			x1: cxValue + (outerRadius + SHIFT_VALUE) * Math.cos(-startAngle * RADIAN),
			y1: cyValue + (outerRadius + SHIFT_VALUE) * Math.sin(-startAngle * RADIAN),
			x2: cxValue + length * Math.cos(-startAngle * RADIAN),
			y2: cyValue + length * Math.sin(-startAngle * RADIAN),
		};

		const endLine = {
			x1: cxValue + (outerRadius + SHIFT_VALUE) * Math.cos(-endAngle * RADIAN),
			y1: cyValue + (outerRadius + SHIFT_VALUE) * Math.sin(-endAngle * RADIAN),
			x2: cxValue + length * Math.cos(-endAngle * RADIAN),
			y2: cyValue + length * Math.sin(-endAngle * RADIAN),
		};

		// Calculate the start point of the line on the Pie border
		const startX = cxValue + (outerRadius + SHIFT_VALUE) * Math.cos(-midAngle * RADIAN);
		const startY = cyValue + (outerRadius + SHIFT_VALUE) * Math.sin(-midAngle * RADIAN);
		// The end point is the extension from the outer border in the direction opposite to the center of the Pie
		const endX = cxValue + length * Math.cos(-midAngle * RADIAN);
		const endY = cyValue + length * Math.sin(-midAngle * RADIAN);

		if (index === 0) {
			return (
				<g>
					<line x1={startX} y1={startY} x2={endX} y2={endY} stroke={labelLineAndOutsideLabelColor} />
					<text
						x={startLine.x2 - (props.isHoveringChart ? 15 : 10)}
						y={startLine.y2}
						style={{ fontWeight: "400", color: "red" }}
						textAnchor="middle"
						dominantBaseline="middle"
						fontSize={11}
						fontFamily={"Montserrat"}
						fill={labelLineAndOutsideLabelColor}
					>
						{props.isHoveringChart ? firstRegularNumbers(roundUpToNearest500(props.dynamicPVRInfo?.minValue)) : 0}
					</text>
					<line {...startLine} stroke={labelLineAndOutsideLabelColor} />
					<line {...endLine} stroke={labelLineAndOutsideLabelColor} />
				</g>
			);
		}

		if (index === 1) {
			return (
				<g>
					<line x1={startX} y1={startY} x2={endX} y2={endY} stroke="#B3B3B3" />
					<text
						x={endX}
						y={endY - 10}
						style={{ fontWeight: "400", color: "red" }}
						textAnchor="middle"
						fill={labelLineAndOutsideLabelColor}
						dominantBaseline="middle"
						fontSize={11}
						fontFamily={"Montserrat"}
					>
						$
						{firstRegularNumbers(
							props.isHoveringChart ? roundUpToNearest500(props.dynamicPVRInfo?.average) || 0 : maxValue / 2
						)}
					</text>
				</g>
			);
		}

		if (index === 2) {
			return (
				<g>
					<line x1={startX} y1={startY} x2={endX} y2={endY} stroke={labelLineAndOutsideLabelColor} />
					<line {...startLine} stroke={labelLineAndOutsideLabelColor} />
					<line {...endLine} stroke={labelLineAndOutsideLabelColor} />
					<text
						x={endLine.x2 + 20}
						y={endLine.y2}
						style={{ fontWeight: "400" }}
						fill={labelLineAndOutsideLabelColor}
						textAnchor="middle"
						dominantBaseline="middle"
						fontSize={11}
						fontFamily={"Montserrat"}
					>
						$
						{firstRegularNumbers(
							props.isHoveringChart || shouldReCalculateAnalytics
								? roundUpToNearest500(props.dynamicPVRInfo?.maxValue)
								: maxValue
						)}
					</text>
				</g>
			);
		}

		return null;
	};

	return (
		<PieChart width={500} height={250} margin={{ top: 15, right: 40, left: 40, bottom: 15 }} style={{ paddingLeft: "40px" }}>
			<defs>
				<linearGradient id="colorA" x1="0" y1="0" x2="0" y2="1">
					<stop offset="15%" stopColor="#7189B8" stopOpacity={1} />
					<stop offset="85%" stopColor="#B3B3B3" stopOpacity={1} />
				</linearGradient>
				<linearGradient id="colorB" x1="1" y1="1" x2="0" y2="1">
					<stop offset="15%" stopColor="#3D67BC" stopOpacity={1} />
					<stop offset="85%" stopColor="#7189B8" stopOpacity={1} />
				</linearGradient>
				<linearGradient id="colorC" x1="0" y1="0" x2="0" y2="1">
					<stop offset="15%" stopColor="#3D67BC" stopOpacity={1} />
					<stop offset="85%" stopColor="#2357BE" stopOpacity={1} />
				</linearGradient>
			</defs>
			<Pie
				style={{ backgroundColor: "white" }}
				dataKey="value"
				startAngle={180}
				endAngle={0}
				data={data}
				cx={cx}
				cy={cy}
				innerRadius={iR}
				outerRadius={oR}
				stroke="none"
				paddingAngle={2}
				cornerRadius={100}
				labelLine={false}
				label={renderCustomizedLabel}
				isAnimationActive={false}
				// @ts-ignore
				margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
			>
				{data.map((entry) => (
					<Cell key={`cell-${entry.name}`} fill={entry.color}></Cell>
				))}
				{/*Custom Components*/}
				<Label
					content={
						<CustomMoneyLabel
							viewBox={{
								cx,
								cy,
							}}
							finalValue={getValue({
								value,
								filteredRows,
								isHoveringChart: props.isHoveringChart,
								speedometerValue: props.speedometerValue,
							})}
						/>
					}
				/>
				<Label
					content={
						<CustomPill
							dynamicPVRInfo={props.dynamicPVRInfo}
							maxValue={maxValue}
							value={value}
							isHoveringChart={props.isHoveringChart}
						/>
					}
				/>
				<Label
					content={
						<AnimatedNeedle
							value={getValue({
								value,
								filteredRows,
								isHoveringChart: props.isHoveringChart,
								speedometerValue: props.speedometerValue,
							})}
							cx={cx}
							cy={cy}
							iR={iR}
							oR={oR}
							color={"#CCCCCC"}
							minValue={props.isHoveringChart ? roundUpToNearest500(props.dynamicPVRInfo?.minValue) : 0}
							maxValue={props.isHoveringChart ? roundUpToNearest500(props.dynamicPVRInfo?.maxValue) : maxValue}
							dynamicPVRInfo={props.dynamicPVRInfo}
						/>
					}
				/>
				<Label
					content={
						<AnimatedNeedle
							value={getValue({
								filteredRows,
								value: currentValue,
								isHoveringChart: props.isHoveringChart,
								speedometerValue: props.speedometerValue,
							})}
							cx={cx}
							cy={cy}
							iR={iR}
							oR={oR}
							color={"#FFA000"}
							minValue={props.isHoveringChart ? roundUpToNearest500(props.dynamicPVRInfo?.minValue) : 0}
							maxValue={props.isHoveringChart ? roundUpToNearest500(props.dynamicPVRInfo?.maxValue) : maxValue}
							dynamicPVRInfo={props.dynamicPVRInfo}
						/>
					}
				/>
			</Pie>
		</PieChart>
	);
}
